import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Building } from 'lucide-react';

const cases = [
  {
    icon: Building,
    title: 'Distressed 80-Acre Cemetery Acquisition',
    summary: 'Complete operational turnaround of a struggling cemetery asset',
    challenge: 'Acquired a distressed 80-acre cemetery with declining burial volumes, outdated facilities, and no pre-need sales infrastructure.',
    actions: [
      'Renovated aging facilities and grounds',
      'Built and trained a dedicated pre-need sales team',
      'Implemented modern operational systems',
      'Developed community outreach programs',
    ],
    results: [
      { metric: 'Burial Volume', before: '~100', after: '300+', unit: 'annually' },
      { metric: 'Pre-Need Pipeline', before: '$0', after: '$2M+', unit: 'active' },
      { metric: 'Asset Position', before: 'Distressed', after: 'Profitable Sale', unit: '' },
    ],
  },
  {
    icon: TrendingUp,
    title: 'Nonprofit Cemetery Turnaround',
    summary: 'Revenue transformation and governance modernization',
    challenge: 'A nonprofit cemetery Board facing revenue decline, outdated systems, and governance challenges threatening long-term sustainability.',
    actions: [
      'Modernized all operational and financial systems',
      'Digitized historical records and mapping',
      'Implemented AI-powered customer service systems',
      'Aligned Board governance with strategic priorities',
      'Developed comprehensive pre-need program',
    ],
    results: [
      { metric: 'Annual Revenue', before: '$600K', after: '$1.4M', unit: '' },
      { metric: 'Operating Model', before: 'Reactive', after: 'Structured', unit: '' },
      { metric: 'Decision Framework', before: 'Ad-hoc', after: 'Governed', unit: '' },
    ],
  },
  {
    icon: Users,
    title: 'Pre-Need Sales Force Development',
    summary: 'Building high-performance insurance distribution',
    challenge: 'Create a commission-based pre-need sales organization from scratch to generate sustainable revenue streams.',
    actions: [
      'Designed agent recruitment and training programs',
      'Established commission structures and incentive systems',
      'Developed compliance frameworks for insurance distribution',
      'Created sales enablement tools and processes',
    ],
    results: [
      { metric: 'Agents Trained', before: '0', after: '100+', unit: 'commission-based' },
      { metric: 'Active Policy Volume', before: '$0', after: '$5M+', unit: '' },
      { metric: 'Compliance Infrastructure', before: 'None', after: 'Multi-market', unit: '' },
    ],
  },
];

export default function CaseResults() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              Case Results
            </h1>
            <p className="text-lg text-gray-300">
              Measurable outcomes from engagements with cemetery and deathcare organizations.
            </p>
          </div>
        </div>
      </section>

      {/* Cases */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-16 max-w-content mx-auto">
            {cases.map((caseStudy, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 ${index > 0 ? 'pt-16 border-t border-gray-100' : ''}`}
              >
                {/* Left Column */}
                <div className="lg:col-span-5">
                  <div className="lg:sticky lg:top-28">
                    <caseStudy.icon className="w-10 h-10 text-accent-600 mb-4" />
                    <h2 className="font-serif text-xl md:text-2xl font-medium text-slate-900 mb-3">
                      {caseStudy.title}
                    </h2>
                    <p className="text-slate-600 mb-4 text-sm">
                      {caseStudy.summary}
                    </p>
                    
                    <div className="mb-4">
                      <h3 className="font-semibold text-slate-900 mb-1 text-sm">Challenge</h3>
                      <p className="text-slate-600 text-xs">
                        {caseStudy.challenge}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1 text-sm">Actions Taken</h3>
                      <ul className="space-y-1">
                        {caseStudy.actions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600 text-xs">
                            <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Right Column - Results */}
                <div className="lg:col-span-7">
                  <div className="bg-slate-900 text-white p-6 rounded-sm">
                    <h3 className="font-serif text-base font-medium mb-4">Measurable Results</h3>
                    <div className="space-y-4">
                      {caseStudy.results.map((result, i) => (
                        <div 
                          key={i}
                          className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-700 last:border-0 last:pb-0"
                        >
                          <div>
                            <p className="text-gray-400 text-xs mb-1">{result.metric}</p>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl md:text-3xl font-serif text-accent-400">
                                {result.after}
                              </span>
                              {result.before !== '' && (
                                <span className="text-slate-500 line-through text-xs">
                                  {result.before}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 sm:mt-0">
                            <span className="text-gray-400 text-xs">{result.unit}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom text-center">
          <h2 className="font-serif text-2xl font-medium text-slate-900 mb-4">
            Face Similar Challenges?
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-8">
            These results demonstrate what's possible with experienced leadership. 
            Let's discuss your organization's specific situation.
          </p>
          <Link to="/contact" className="btn-primary">
            Schedule a Confidential Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
